/**
 * Theme model for DeployHub.
 *
 * Three user-facing modes:
 * - `light` / `dark` — an explicit user override.
 * - `auto` (default) — follow the OS `prefers-color-scheme`.
 *
 * The override is persisted in localStorage under `deployhub.theme`. `auto` is
 * represented by the ABSENCE of a stored value, so clearing storage returns the
 * user to the system default. The effective (resolved) theme is always `light`
 * or `dark` and is applied by toggling the `dark` class on <html> — matching
 * Tailwind's `darkMode: 'class'` strategy. An inline script in index.html applies
 * the same resolution before first paint to prevent a flash of the wrong theme.
 */

/** A user-selectable theme mode. */
export type ThemeMode = 'light' | 'dark' | 'auto';

/** The concrete theme actually rendered (never `auto`). */
export type ResolvedTheme = 'light' | 'dark';

/** localStorage key holding the user's explicit override, if any. */
export const THEME_STORAGE_KEY = 'deployhub.theme';

/** Ordered cycle used by the toggle: light → dark → auto → light … */
export const THEME_CYCLE: readonly ThemeMode[] = ['light', 'dark', 'auto'];

/**
 * Read the persisted theme mode. Returns `auto` when nothing is stored or the
 * stored value is unrecognised, and degrades gracefully if storage is unavailable
 * (e.g. privacy mode / SSR-less test environments).
 */
export function readStoredMode(): ThemeMode {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    if (value === 'light' || value === 'dark') {
      return value;
    }
  } catch {
    /* localStorage inaccessible — fall through to auto. */
  }
  return 'auto';
}

/**
 * Persist the theme mode. `auto` removes the key (returning to system default);
 * explicit modes are written. Storage failures are swallowed so the UI never
 * breaks when persistence is unavailable.
 */
export function persistMode(mode: ThemeMode): void {
  try {
    if (mode === 'auto') {
      localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    }
  } catch {
    /* Persistence is best-effort; ignore failures. */
  }
}

/** Whether the OS currently prefers a dark colour scheme. */
export function systemPrefersDark(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/**
 * Resolve a mode to the concrete theme to render. `auto` consults the OS
 * preference; explicit modes are returned as-is.
 */
export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'auto') {
    return systemPrefersDark() ? 'dark' : 'light';
  }
  return mode;
}

/** Toggle the `dark` class on <html> to match the resolved theme. */
export function applyTheme(resolved: ResolvedTheme): void {
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

/** Return the next mode in the toggle cycle. */
export function nextMode(mode: ThemeMode): ThemeMode {
  const index = THEME_CYCLE.indexOf(mode);
  return THEME_CYCLE[(index + 1) % THEME_CYCLE.length] ?? 'auto';
}
